import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@bcc/components/ui/card";
import { Checkbox } from "@bcc/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@bcc/components/ui/tooltip";
import {
  Download,
  ExternalLink,
  Eye,
  FileText,
  MoreHorizontal,
  Printer,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchDetailModal } from "./batch-detail-modal";
import { OrderDetailDialog } from "./order-detail-dialog";
import { setOrdersTableTestId } from "./orders-table.utils";

const carriers = ["USPS", "UPS"] as const;
type Carrier = (typeof carriers)[number];

interface OrderData {
  id: string;
  customer: string;
  product: string;
  quantity: number;
  weight: number;
  amount: string;
  status: "completed" | "waiting" | "shipped" | "cancelled";
  date: string;
  age: string;
  marketplace: string;
  tracking: string | null;
  batch: string | null;
  carrier: Carrier;
}

const statusConfig = {
  completed: {
    label: "Fulfilled",
    className:
      "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-950 dark:text-green-400",
  },
  waiting: {
    label: "Waiting to be Fulfilled",
    className:
      "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-400",
  },
  shipped: {
    label: "Shipped",
    className:
      "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-950 dark:text-blue-400",
  },
  cancelled: {
    label: "Cancelled",
    className:
      "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-950 dark:text-red-400",
  },
};

const getCarrierTrackingUrl = (
  carrier: Carrier,
  trackingNumber: string,
): string => {
  if (carrier === "USPS") {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
  } else {
    return `https://www.ups.com/track?tracknum=${trackingNumber}`;
  }
};

interface OrdersTableProps {
  data: OrderData[];
  statusFilter: string;
  selectedOrders: string[];
  onSelectionChange: (orders: string[]) => void;
  disableSelection?: boolean;
  testId?: string;
}

// Batch Actions Component
const BatchActions = ({
  batch,
  onViewBatch,
  t,
}: {
  batch: string;
  onViewBatch: (batch: string) => void;
  t: (key: string) => string;
}) => (
  <TooltipProvider>
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onViewBatch(batch)}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("views.ordersTable.tooltips.view")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              console.log(
                "[v0] Downloading shipping labels & packing slips for batch:",
                batch,
              );
            }}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t("views.ordersTable.tooltips.downloadLabels")}
        </TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              console.log("[v0] Viewing summary for batch:", batch);
            }}
          >
            <FileText className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t("views.ordersTable.tooltips.summary")}
        </TooltipContent>
      </Tooltip>
    </div>
  </TooltipProvider>
);

// Cell Components
function SelectionCell({
  orderId,
  isSelected,
  onSelect,
}: {
  orderId: string;
  isSelected: boolean;
  onSelect: (orderId: string, checked: boolean) => void;
}) {
  return (
    <td className="px-4 py-4">
      <Checkbox
        checked={isSelected}
        onCheckedChange={(checked) => onSelect(orderId, checked as boolean)}
      />
    </td>
  );
}

function OrderIdCell({
  orderId,
  onViewOrder,
}: {
  orderId: string;
  onViewOrder: () => void;
}) {
  return (
    <td className="px-4 py-4">
      <button
        onClick={onViewOrder}
        className="font-mono text-sm font-medium text-primary hover:underline"
      >
        {orderId}
      </button>
    </td>
  );
}

function CreatedDateCell({ date }: { date: string }) {
  return (
    <td className="px-4 py-4">
      <span className="text-sm text-muted-foreground">{date}</span>
    </td>
  );
}

function AgeCell({ age }: { age: string }) {
  return (
    <td className="px-4 py-4">
      <span className="text-sm text-muted-foreground">{age}</span>
    </td>
  );
}

function StatusCell({
  status,
  t,
}: {
  status: "completed" | "waiting" | "shipped" | "cancelled";
  t: (key: string) => string;
}) {
  const statusLabels = {
    completed: t("views.ordersTable.status.fulfilled"),
    waiting: t("views.ordersTable.status.waiting"),
    shipped: t("views.ordersTable.status.shipped"),
    cancelled: t("views.ordersTable.status.cancelled"),
  };
  return (
    <td className="px-4 py-4">
      <Badge variant="secondary" className={statusConfig[status].className}>
        {statusLabels[status]}
      </Badge>
    </td>
  );
}

function BatchCell({
  batch,
  onViewBatch,
  t,
}: {
  batch: string | null;
  onViewBatch: (batch: string) => void;
  t: (key: string) => string;
}) {
  return (
    <td className="px-4 py-4">
      {batch ? (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewBatch(batch)}
            className="text-sm font-mono text-primary hover:underline"
          >
            {batch}
          </button>
          <BatchActions batch={batch} onViewBatch={onViewBatch} t={t} />
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </td>
  );
}

function MarketplaceCell({ marketplace }: { marketplace: string }) {
  return (
    <td className="px-4 py-4">
      <span className="text-sm">{marketplace}</span>
    </td>
  );
}

function ItemsCell({ quantity }: { quantity: number }) {
  return (
    <td className="px-4 py-4">
      <span className="text-sm">{quantity} bags</span>
    </td>
  );
}

function WeightCell({ weight }: { weight: number }) {
  return (
    <td className="px-4 py-4">
      <span className="text-sm text-muted-foreground">{weight} oz</span>
    </td>
  );
}

function TrackingCell({
  tracking,
  carrier,
}: {
  tracking: string | null;
  carrier: Carrier;
}) {
  return (
    <td className="px-4 py-4">
      {tracking ? (
        <a
          href={getCarrierTrackingUrl(carrier, tracking)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-mono text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          {tracking}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </td>
  );
}

function ActionsCell({
  order,
  disableSelection,
  onViewOrder,
  t,
}: {
  order: OrderData;
  disableSelection?: boolean;
  onViewOrder: () => void;
  t: (key: string) => string;
}) {
  return (
    <td className="px-4 py-4 text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">
              {t("views.ordersTable.tooltips.openMenu")}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onViewOrder}>
            <Eye className="mr-2 h-4 w-4" />
            {t("views.ordersTable.actions.viewDetails")}
          </DropdownMenuItem>
          {!disableSelection && (
            <DropdownMenuItem>
              <Printer className="mr-2 h-4 w-4" />
              {t("views.ordersTable.actions.printLabel")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </td>
  );
}

// Order Row Component
interface OrderRowProps {
  order: OrderData;
  isSelected: boolean;
  disableSelection?: boolean;
  onSelectOrder: (orderId: string, checked: boolean) => void;
  onViewOrder: (order: OrderData) => void;
  onViewBatch: (batch: string) => void;
  t: (key: string) => string;
}

function OrderRow({
  order,
  isSelected,
  disableSelection,
  onSelectOrder,
  onViewOrder,
  onViewBatch,
  t,
}: OrderRowProps) {
  return (
    <tr className="hover:bg-muted/50 transition-colors">
      {!disableSelection && (
        <SelectionCell
          orderId={order.id}
          isSelected={isSelected}
          onSelect={onSelectOrder}
        />
      )}
      <OrderIdCell orderId={order.id} onViewOrder={() => onViewOrder(order)} />
      <CreatedDateCell date={order.date} />
      <AgeCell age={order.age} />
      <StatusCell status={order.status} t={t} />
      <BatchCell batch={order.batch} onViewBatch={onViewBatch} t={t} />
      <MarketplaceCell marketplace={order.marketplace} />
      <ItemsCell quantity={order.quantity} />
      <WeightCell weight={order.weight} />
      <TrackingCell tracking={order.tracking} carrier={order.carrier} />
      <ActionsCell
        order={order}
        disableSelection={disableSelection}
        onViewOrder={() => onViewOrder(order)}
        t={t}
      />
    </tr>
  );
}

// Carrier Section Component
const CarrierSection = ({
  carrier,
  carrierOrders,
  selectedOrders,
  disableSelection,
  onSelectAll,
  onSelectOrder,
  onViewOrder,
  onViewBatch,
  t,
}: {
  carrier: Carrier;
  carrierOrders: OrderData[];
  selectedOrders: string[];
  disableSelection?: boolean;
  onSelectAll: (checked: boolean, carrierOrders: OrderData[]) => void;
  onSelectOrder: (orderId: string, checked: boolean) => void;
  onViewOrder: (order: OrderData) => void;
  onViewBatch: (batch: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => (
  <Card className="mb-6">
    <CardHeader className="pb-3">
      <CardTitle className="text-lg font-semibold flex items-center gap-2">
        {carrier}
        <Badge variant="secondary" className="ml-2">
          {t("views.ordersTable.units.orders", { count: carrierOrders.length })}
        </Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              {!disableSelection && (
                <th className="px-4 py-4 text-left">
                  <Checkbox
                    checked={
                      carrierOrders.every((order) =>
                        selectedOrders.includes(order.id),
                      ) && carrierOrders.length > 0
                    }
                    onCheckedChange={(checked) =>
                      onSelectAll(checked as boolean, carrierOrders)
                    }
                  />
                </th>
              )}
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.order")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.created")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.age")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.status")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.batch")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.marketplace")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.items")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.weight")}
              </th>
              <th className="px-4 py-4 text-left text-sm font-semibold">
                {t("views.ordersTable.columns.tracking")}
              </th>
              <th className="px-4 py-4 text-right text-sm font-semibold">
                {t("views.ordersTable.columns.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {carrierOrders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                isSelected={selectedOrders.includes(order.id)}
                disableSelection={disableSelection}
                onSelectOrder={onSelectOrder}
                onViewOrder={onViewOrder}
                onViewBatch={onViewBatch}
                t={t}
              />
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

// Main Table Component
export function OrdersTable({
  data,
  statusFilter,
  selectedOrders,
  onSelectionChange,
  disableSelection,
  testId,
}: OrdersTableProps) {
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const { t } = useTranslation();

  const filteredOrders = data.filter((order) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "waiting") return order.status === "waiting";
    if (statusFilter === "fulfilled") return order.status === "completed";
    return order.status === statusFilter;
  });

  const uspsOrders = filteredOrders.filter((order) => order.carrier === "USPS");
  const upsOrders = filteredOrders.filter((order) => order.carrier === "UPS");

  const handleSelectAll = (checked: boolean, carrierOrders: OrderData[]) => {
    if (checked) {
      const newSelections = [
        ...new Set([...selectedOrders, ...carrierOrders.map((o) => o.id)]),
      ];
      onSelectionChange(newSelections);
    } else {
      const carrierOrderIds = carrierOrders.map((o) => o.id);
      onSelectionChange(
        selectedOrders.filter((id) => !carrierOrderIds.includes(id)),
      );
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedOrders, orderId]);
    } else {
      onSelectionChange(selectedOrders.filter((id) => id !== orderId));
    }
  };

  return (
    <>
      <div
        data-testid={testId ?? setOrdersTableTestId("container")}
        className="space-y-6"
      >
        {uspsOrders.length > 0 && (
          <CarrierSection
            carrier="USPS"
            carrierOrders={uspsOrders}
            selectedOrders={selectedOrders}
            disableSelection={disableSelection}
            onSelectAll={handleSelectAll}
            onSelectOrder={handleSelectOrder}
            onViewOrder={setSelectedOrder}
            onViewBatch={setSelectedBatch}
            t={t as (key: string) => string}
          />
        )}
        {upsOrders.length > 0 && (
          <CarrierSection
            carrier="UPS"
            carrierOrders={upsOrders}
            selectedOrders={selectedOrders}
            disableSelection={disableSelection}
            onSelectAll={handleSelectAll}
            onSelectOrder={handleSelectOrder}
            onViewOrder={setSelectedOrder}
            onViewBatch={setSelectedBatch}
            t={t as (key: string) => string}
          />
        )}
      </div>

      <OrderDetailDialog
        order={selectedOrder}
        open={!!selectedOrder}
        onOpenChange={(open) => !open && setSelectedOrder(null)}
      />

      <BatchDetailModal
        batchId={selectedBatch}
        open={!!selectedBatch}
        onOpenChange={(open) => !open && setSelectedBatch(null)}
      />
    </>
  );
}
